/**
 * Invoices Management Page
 * Displays all invoices across brands with generation, approval, and sending capabilities
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Box,
  BlockStack,
  InlineStack,
  Badge,
  Button,
  Spinner,
  Banner,
  EmptyState,
  IndexTable,
  Tooltip,
  Modal,
  TextField,
  Select,
  FormLayout,
  Divider,
} from '@shopify/polaris';
import {
  RefreshIcon,
  EmailIcon,
  CheckIcon,
  CashDollarIcon,
  DeleteIcon,
  ReceiptIcon,
  CalendarIcon,
  ViewIcon,
} from '@shopify/polaris-icons';

import { useBrands } from '../utils/queries';
import apiClient from '../utils/api';
import { headingsFor, emptyRow, PageSizeSelect } from '../utils/table';
import { autoUpdateBadge, describeAutoUpdate } from '../utils/invoice-auto-updates';
import { hasStorage, storageFeeOf, storageHint } from '../utils/storage-line';

// Default ordering sent to GET /invoices. Constants, not state - see the note at their use site.
const INVOICE_SORT_BY = 'createdAt';
const INVOICE_SORT_DIRECTION = 'descending';

/**
 * Column definitions, so the table keeps its headings when there are no invoices.
 * See utils/table.jsx.
 */
const INVOICE_COLUMNS = [
  { key: 'number', title: 'Invoice #', tip: 'The invoice number. Generated on the 1st of each month for every configured brand.' },
  { key: 'brand', title: 'Brand', tip: 'The brand being invoiced.' },
  { key: 'status', title: 'Status', tip: 'Where the invoice is: drafted, sent, paid or overdue. Sending is a deliberate action, not automatic.' },
  { key: 'period', title: 'Period', tip: 'The month the invoice covers, which is the month orders were processed in — not necessarily the month they shipped.' },
  { key: 'amount', title: 'Amount', tip: 'The total billed: commission, shipping and return fees, less any adjustment.' },
  { key: 'issue', title: 'Issue Date', tip: 'When the invoice was generated.' },
  { key: 'due', title: 'Due Date', tip: 'When payment is due, from the brand’s configured terms.' },
  { key: 'actions', title: 'Actions', tip: 'View, send or download this invoice.' },
];

export default function Invoices() {
  // State management
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Filter states
  const [brandFilter, setBrandFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  // Bumped to force a refetch. The generation job is asynchronous, so the list has to be
  // re-read a moment after triggering — and a setTimeout(fetchInvoices) captured the
  // fetchInvoices from the render where the filters were still the OLD ones, so it re-queried
  // the month the user had just moved away from.
  const [refreshNonce, setRefreshNonce] = useState(0);
  // All months by default, NOT the previous one.
  //
  // A page whose job is to show invoices must not open on a filter that hides them. Defaulting
  // to the previous month assumed invoices are always generated for it, and the moment one is
  // generated for any other month — August, in the case that surfaced this — the page opens on
  // an empty July and reads "No invoices found" with a Trigger button, which looks like nothing
  // has ever been generated. The invoice was there the whole time, one month over.
  //
  // 'All Months' also stays honest as the data grows: whatever the newest invoice is, it is on
  // screen when the page opens, and narrowing by month is one click away.
  const [monthFilter, setMonthFilter] = useState('');
  // Not state: no control exists to change it. setSortBy/setSortDirection were never called
  // anywhere, and the IndexTable below declares no `sortable`/`onSort`, so these were a
  // permanently frozen pair of values dressed up as state and listed in fetchInvoices'
  // dependency array. They are the API's default ordering, so they stay - as constants that say
  // what they are. Wiring real column sorting would mean adding sortable/onSort to the table;
  // GET /invoices already accepts sortBy/sortDirection, so nothing backend-side blocks it.
  const sortBy = INVOICE_SORT_BY;
  const sortDirection = INVOICE_SORT_DIRECTION;

  // Modal states
  const [generateModalActive, setGenerateModalActive] = useState(false);
  const [detailsModalActive, setDetailsModalActive] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [pdfDownloadingId, setPdfDownloadingId] = useState(null); // Track which invoice PDF is being downloaded

  // Trigger monthly invoices modal form state
  const [triggerForm, setTriggerForm] = useState({
    monthString: '', // Format: YYYY-MM
    brandId: '',     // Empty means every brand with complete invoice settings
  });

  // Errors from the trigger, kept separate from the page-level banner because the modal is a
  // portal with a backdrop: anything rendered inside <Layout> is painted UNDERNEATH it. The
  // server goes to real trouble to say why a brand cannot be invoiced, and that sentence has
  // to land where the person who caused it is looking.
  const [triggerError, setTriggerError] = useState('');

  // The trigger's own in-flight flag. `actionLoading` is shared with the row actions and
  // holds an INVOICE ID while one is approving (see handleApproveInvoice), so reading it as a
  // boolean here would disable this modal's buttons because of an unrelated row.
  const [triggerLoading, setTriggerLoading] = useState(false);

  /** Close the trigger modal and drop everything it was holding. */
  const closeTriggerModal = () => {
    setGenerateModalActive(false);
    setTriggerForm({ monthString: '', brandId: '' });
    setTriggerError('');
  };

  /** Open it clean, so a message from an abandoned run is never the first thing shown. */
  const openTriggerModal = () => {
    setTriggerError('');
    setGenerateModalActive(true);
  };

  /** One place to raise a trigger failure, so a stale success can never outlive it. */
  const failTrigger = (message) => {
    setSuccessMessage('');
    setTriggerError(message);
  };

  // Send invoice modal state
  const [sendModalActive, setSendModalActive] = useState(false);
  // Comma-separated override. Empty means "use the brand's configured recipients",
  // which is what the API does when it receives null.
  const [sendRecipients, setSendRecipients] = useState('');

  // Payment modal state
  const [paymentModalActive, setPaymentModalActive] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    paymentMethod: '',
    reference: ''
  });

  /**
   * Rows per page — and the API's `limit`.
   *
   * The invoice LIST is paged SERVER-SIDE: GET /invoices takes `page` and `limit` and answers
   * with `pagination.totalPages`, so the size chosen here goes into the request and
   * `fetchInvoices` re-fires (it is one of its dependencies). Slicing the returned array
   * instead would show 5 of the 20 invoices the server sent while the pager still offered
   * pages of 20 — and `totalPages` comes from the server, so nothing local could correct it.
   *
   * Seeded at 20, the size this screen has always asked for; not DEFAULT_PAGE_SIZE, which
   * would quietly halve the window for everyone already used to this page.
   *
   * KNOWN WRINKLE, same as Access Requests: 20 is not one of the shared control's options
   * (5/10/25/50 in utils/table.jsx, which is not ours to change), so until someone picks a
   * size the Select has no option matching the seed and displays its first one. The list
   * itself is right — 20 invoices are requested and 20 are shown — and choosing any value
   * makes control and table agree for good. Adding 20 to PAGE_SIZE_OPTIONS would fix it.
   */
  const [itemsPerPage, setItemsPerPage] = useState(20);

  /**
   * A new row count restarts at page 1, exactly as the three filters above already do: page 3
   * of a 20-row window sits inside page 1 of a 50-row one, and asking the server for a page
   * past the end answers with an empty list under a table that has invoices in it.
   */
  const handlePageSizeChange = useCallback((size) => {
    setItemsPerPage(size);
    setCurrentPage(1);
  }, []);

  // Fetch brands for dropdown
  const { data: brandsResponse, isLoading: brandsLoading } = useBrands();
  const brands = brandsResponse?.brands || brandsResponse || [];

  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortDirection,
      };

      if (brandFilter) params.brandId = brandFilter;
      if (statusFilter) params.status = statusFilter;
      if (monthFilter) params.month = monthFilter;

      const response = await apiClient.getInvoices(params);

      if (response.success) {
        setInvoices(response.invoices || []);
        setTotalPages(response.pagination?.totalPages || 1);
      } else {
        setError(response.error || 'Failed to load invoices');
      }
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError(err.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, brandFilter, statusFilter, monthFilter, sortBy, sortDirection, refreshNonce]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Brand options for filter/generate
  const brandOptions = useMemo(() => {
    return [
      { label: 'All Brands', value: '' },
      ...brands.map(brand => ({
        label: brand.name,
        value: brand.id
      }))
    ];
  }, [brands]);

  // Status options
  const statusOptions = [
    { label: 'All Statuses', value: '' },
    { label: 'Draft', value: 'DRAFT' },
    { label: 'Approved', value: 'APPROVED' },
    { label: 'Sent', value: 'SENT' },
    { label: 'Paid', value: 'PAID' },
    { label: 'Cancelled', value: 'CANCELLED' },
  ];

  // Month options (last 24 months)
  const monthOptions = useMemo(() => {
    const options = [{ label: 'All Months', value: '' }];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthString = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
      const monthLabel = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      options.push({ label: monthLabel, value: monthString });
    }
    return options;
  }, []);

  // Get status badge tone
  const getStatusBadge = (status) => {
    const statusConfig = {
      DRAFT: { tone: 'attention', label: 'Draft' },
      APPROVED: { tone: 'info', label: 'Approved' },
      SENT: { tone: 'warning', label: 'Sent' },
      PAID: { tone: 'success', label: 'Paid' },
      CANCELLED: { tone: 'critical', label: 'Cancelled' },
    };
    const config = statusConfig[status] || { tone: 'default', label: status };
    return <Badge tone={config.tone}>{config.label.toLowerCase() === "sent" ? "Approved/Awaiting Payment" : config.label}</Badge>;
  };

  // Currency for the invoice being previewed.
  //
  // Was a hardcoded 'USD' stamped on every figure including TOTAL PAYABLE TO 2W. The brand's
  // real currency is Brand.currencyCode (schema.prisma:47, String @default("USD")), so this now
  // reads it off the payload.
  //
  // BACKEND FOLLOW-UP (cannot be done from the console): invoice-service.js does not select
  // currencyCode in either brand include - getInvoices (line 586-595) and getInvoice (line
  // 618-628) select only id/name/shopDomain/invoicePaymentMethod. Until `currencyCode` is added
  // to those two selects this falls back to the column's own default, 'USD'. The fallback is the
  // schema default rather than an invented value, and it disappears on its own once the field
  // ships - no further console change needed.
  const invoiceCurrency = selectedInvoice?.brand?.currencyCode || 'USD';

  // Format currency
  const formatCurrency = (amount, currency = invoiceCurrency) => {
    const num = parseFloat(amount) || 0;
    return `${currency} ${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format negative currency (puts minus after the code: USD -1,234.56)
  const formatNegativeCurrency = (amount, currency = invoiceCurrency) => {
    const num = parseFloat(amount) || 0;
    return `${currency} ${num > 0 ? '-' : ''}${Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format date (using UTC to prevent timezone shifts)
  const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
  };

  // Handle trigger monthly invoices — every configured brand, or one named brand
  const handleTriggerMonthlyInvoices = async () => {
    // "Previous month" is a sensible default for a sweep across every brand, because that is
    // what the monthly schedule means. Asked for one brand it is a guess, and the guess lands
    // on an invoice, so make the month explicit instead.
    if (triggerForm.brandId && !triggerForm.monthString) {
      failTrigger('Choose a month when generating for a single brand');
      return;
    }

    setTriggerLoading(true);
    setTriggerError('');

    try {
      const response = await apiClient.triggerMonthlyInvoices({
        monthString: triggerForm.monthString || null, // null = previous month
        brandId: triggerForm.brandId || null,         // null = every configured brand
      });

      if (response.success) {
        setSuccessMessage(response.message || 'Monthly invoice generation triggered successfully');
        closeTriggerModal();

        // Point the list at what was just generated. Every filter that could hide it has to
        // move together: a status filter left on Paid, or a page other than the first, shows
        // an empty table under a success banner and reads as though nothing was created.
        setBrandFilter(response.brandId || '');
        // Generation only ever produces a DRAFT, so a Draft filter cannot hide the result and
        // is left alone — clearing it would throw away a narrowing the admin chose. Any other
        // status filter WOULD hide it, so that one goes.
        if (statusFilter && statusFilter !== 'DRAFT') setStatusFilter('');
        setCurrentPage(1);

        // Show the month that was just generated.
        //
        // Without this the run looked like it had failed. The filter defaults to the PREVIOUS
        // month, so generating any other month — August, say — completed successfully and left
        // the page reading "No invoices found", because it was still filtered on July. The
        // month comes from the server's response rather than from triggerForm, so an
        // unspecified month resolves to whatever the job actually chose instead of this screen
        // working it out a second time and possibly differently.
        if (response.month) setMonthFilter(response.month);

        // The job is asynchronous, so one read now would be too early. The nonce is what makes
        // this re-read with the filter set just above rather than with the one captured when
        // this handler was created.
        setTimeout(() => setRefreshNonce((n) => n + 1), 2500);
      } else {
        failTrigger(response.error || 'Failed to trigger invoice generation');
      }
    } catch (err) {
      // The modal stays OPEN on failure. The server's 400 names the one thing to change —
      // a report that is not ready, missing invoice settings, a month already invoiced — and
      // every one of those is fixed by editing this form or going elsewhere and coming back.
      // Closing the modal would throw that sentence away along with what was typed.
      console.error('Error triggering monthly invoices:', err);
      failTrigger(err.message || 'Failed to trigger invoice generation');
    } finally {
      setTriggerLoading(false);
    }
  };

  // Handle approve invoice
  const handleApproveInvoice = async (invoiceId) => {
    setActionLoading(invoiceId);
    setError('');

    try {
      const response = await apiClient.approveInvoice(invoiceId);

      if (response.success) {
        setSuccessMessage('Invoice approved successfully');
        fetchInvoices();
        if (detailsModalActive) {
          setSelectedInvoice(response.invoice);
        }
      } else {
        setError(response.error || 'Failed to approve invoice');
      }
    } catch (err) {
      console.error('Error approving invoice:', err);
      setError(err.message || 'Failed to approve invoice');
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Send the invoice to the brand.
   *
   * This existed as a button with nothing behind it: setSendModalActive(true) was
   * called, no modal was ever bound to that state, and apiClient.sendInvoice had no
   * callers anywhere. Clicking Send closed the preview and silently did nothing, so an
   * invoice could never leave APPROVED — and nothing told the admin that.
   *
   * Recipients are optional: the API falls back to the brand's configured invoice
   * recipients when null. The override box exists for the case where an admin needs to
   * send a copy somewhere else without changing the brand's standing configuration.
   */
  const handleSendInvoice = async () => {
    if (!selectedInvoice) return;

    const recipients = sendRecipients
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean);

    setActionLoading(true);
    setError('');

    try {
      const response = await apiClient.sendInvoice(
        selectedInvoice.id,
        recipients.length ? recipients : null,
      );

      // The endpoint has two success shapes. The normal path returns `success: true`; the
      // already-sent idempotency path returns `{ invoice, email: { alreadySent: true } }`
      // with no `success` key at all, so checking `response.success` alone reported a
      // perfectly good resend as a failure. Treat either shape as success.
      const sent = response.success || Boolean(response.invoice || response.email);

      if (sent) {
        setSuccessMessage(
          recipients.length
            ? `Invoice sent to ${recipients.join(', ')}`
            : 'Invoice sent to the brand’s configured recipients',
        );
        setSendModalActive(false);
        setSendRecipients('');
        fetchInvoices();
      } else {
        setError(response.error || 'Failed to send invoice');
      }
    } catch (err) {
      console.error('Error sending invoice:', err);
      setError(err.message || 'Failed to send invoice');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle record payment
  const handleRecordPayment = async () => {
    if (!selectedInvoice) return;

    setActionLoading(true);
    setError('');

    try {
      // The endpoint reads `method`, not `paymentMethod` — sending the form as-is meant
      // Prisma skipped the column and the typed payment method was silently discarded.
      const response = await apiClient.recordInvoicePayment(selectedInvoice.id, {
        method: paymentForm.paymentMethod,
        reference: paymentForm.reference,
      });

      if (response.success) {
        setSuccessMessage('Payment recorded successfully');
        setPaymentModalActive(false);
        setPaymentForm({ paymentMethod: '', reference: '' });
        fetchInvoices();
        if (detailsModalActive) {
          setSelectedInvoice(response.invoice);
        }
      } else {
        setError(response.error || 'Failed to record payment');
      }
    } catch (err) {
      console.error('Error recording payment:', err);
      setError(err.message || 'Failed to record payment');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle cancel invoice
  const handleCancelInvoice = async (invoiceId) => {
    if (!confirm('Are you sure you want to cancel this invoice?')) return;

    setActionLoading(true);
    setError('');

    try {
      const response = await apiClient.cancelInvoice(invoiceId);

      if (response.success) {
        setSuccessMessage('Invoice cancelled');
        fetchInvoices();
        if (detailsModalActive) {
          setDetailsModalActive(false);
          setSelectedInvoice(null);
        }
      } else {
        setError(response.error || 'Failed to cancel invoice');
      }
    } catch (err) {
      console.error('Error cancelling invoice:', err);
      setError(err.message || 'Failed to cancel invoice');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle delete invoice
  const handleDeleteInvoice = async (invoiceId) => {
    if (!confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) return;

    setActionLoading(true);
    setError('');

    try {
      const response = await apiClient.deleteInvoice(invoiceId);

      if (response.success) {
        setSuccessMessage('Invoice deleted successfully');
        fetchInvoices();
        if (detailsModalActive) {
          setDetailsModalActive(false);
          setSelectedInvoice(null);
        }
      } else {
        setError(response.error || 'Failed to delete invoice');
      }
    } catch (err) {
      console.error('Error deleting invoice:', err);
      setError(err.message || 'Failed to delete invoice');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle regenerate invoice (only DRAFT)
  const handleRegenerateInvoice = async (invoiceId) => {
    if (!confirm('Are you sure you want to regenerate this invoice? This will update the invoice with the latest billing data.')) return;

    setActionLoading(true);
    setError('');

    try {
      const response = await apiClient.regenerateInvoice(invoiceId);

      if (response.success) {
        setSuccessMessage('Invoice regenerated successfully');
        fetchInvoices();
        if (detailsModalActive) {
          setSelectedInvoice(response.invoice);
        }
      } else {
        setError(response.error || 'Failed to regenerate invoice');
      }
    } catch (err) {
      console.error('Error regenerating invoice:', err);
      setError(err.message || 'Failed to regenerate invoice');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle download PDF with loading state
  const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
    try {
      setPdfDownloadingId(invoiceId);
      const blob = await apiClient.downloadInvoicePdf(invoiceId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setError('Failed to download PDF');
    } finally {
      setPdfDownloadingId(null);
    }
  };

  // View invoice details
  const handleViewDetails = async (invoice) => {
    try {
      const response = await apiClient.getInvoice(invoice.id);
      if (response.success) {
        setSelectedInvoice(response.invoice);
        setDetailsModalActive(true);
      }
    } catch (err) {
      console.error('Error fetching invoice details:', err);
      setError('Failed to load invoice details');
    }
  };

  // Table rows
  const rowMarkup = invoices.map((invoice, index) => (
    <IndexTable.Row
      id={invoice.id}
      key={invoice.id}
      position={index}
    >
      <IndexTable.Cell>
        <Button variant="plain" onClick={() => handleViewDetails(invoice)}>
          {invoice.invoiceNumber}
        </Button>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd">{invoice.brand?.name || '-'}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <InlineStack gap="100" wrap={false}>
          {getStatusBadge(invoice.status)}
          {/* Rebuilt by 2W for late retailer refunds, or a late refund missing from it. */}
          {autoUpdateBadge(invoice) && (
            <Badge tone={autoUpdateBadge(invoice).tone}>{autoUpdateBadge(invoice).label}</Badge>
          )}
        </InlineStack>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd">{formatDate(invoice.periodStart)} - {formatDate(invoice.periodEnd)}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd" fontWeight="semibold">{formatCurrency(invoice.totalAmount)}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd">{formatDate(invoice.issueDate)}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd">{formatDate(invoice.dueDate)}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <InlineStack gap="200">
          <Tooltip content="View Details">
            <Button
              icon={ViewIcon}
              variant="plain"
              onClick={() => handleViewDetails(invoice)}
              accessibilityLabel="View details"
            />
          </Tooltip>
          <Tooltip content="Download PDF">
            <Button
              icon={ReceiptIcon}
              variant="plain"
              onClick={() => handleDownloadPdf(invoice.id, invoice.invoiceNumber)}
              accessibilityLabel="Download PDF"
              loading={pdfDownloadingId === invoice.id}
              disabled={pdfDownloadingId !== null}
            />
          </Tooltip>
          {invoice.status === 'DRAFT' && (
            <Tooltip content="Regenerate">
              <Button
                icon={RefreshIcon}
                variant="plain"
                onClick={() => handleRegenerateInvoice(invoice.id)}
                accessibilityLabel="Regenerate invoice"
              />
            </Tooltip>
          )}
          {invoice.status === 'DRAFT' && (
            <Tooltip content="Approve">
              <Button
                icon={CheckIcon}
                variant="plain"
                tone="success"
                onClick={() => handleApproveInvoice(invoice.id)}
                accessibilityLabel="Approve invoice"
                loading={actionLoading == invoice.id}
              />
            </Tooltip>
          )}
          {/* SENT only: the status machine allows PAID from SENT alone, so offering this on
              an APPROVED invoice guaranteed a 500. Approve, send, then record payment. */}
          {invoice.status === 'SENT' && (
            <Tooltip content="Record Payment">
              <Button
                icon={CashDollarIcon}
                variant="plain"
                tone="success"
                onClick={() => {
                  setSelectedInvoice(invoice);
                  setPaymentForm(prev => ({ ...prev, paymentMethod: invoice?.brand?.invoicePaymentMethod || "" }))
                  setPaymentModalActive(true);
                }}
                accessibilityLabel="Record payment"
              />
            </Tooltip>
          )}
          {(invoice.status === 'DRAFT' || invoice.status === 'CANCELLED') && (
            <Tooltip content="Delete">
              <Button
                icon={DeleteIcon}
                variant="plain"
                tone="critical"
                onClick={() => handleDeleteInvoice(invoice.id)}
                accessibilityLabel="Delete invoice"
              />
            </Tooltip>
          )}
        </InlineStack>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <Page
      title="Invoices"
      subtitle="Manage and send invoices to brands (auto-generated monthly)"
      primaryAction={{
        content: 'Trigger Monthly Invoices',
        icon: CalendarIcon,
        onAction: openTriggerModal,
      }}
      secondaryActions={[
        {
          content: 'Refresh',
          icon: RefreshIcon,
          onAction: fetchInvoices,
        }
      ]}
      fullWidth
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError('')}>
              {error}
            </Banner>
          </Layout.Section>
        )}

        {successMessage && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setSuccessMessage('')}>
              {successMessage}
            </Banner>
          </Layout.Section>
        )}

        {/* Filters */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <InlineStack gap="400" wrap={false}>
                <Box minWidth="200px">
                  <Select
                    label="Brand"
                    options={brandOptions}
                    value={brandFilter}
                    onChange={(value) => {
                      setBrandFilter(value);
                      setCurrentPage(1);
                    }}
                  />
                </Box>
                <Box minWidth="150px">
                  <Select
                    label="Status"
                    options={statusOptions}
                    value={statusFilter}
                    onChange={(value) => {
                      setStatusFilter(value);
                      setCurrentPage(1);
                    }}
                  />
                </Box>
                <Box minWidth="180px">
                  <Select
                    label="Month"
                    options={monthOptions}
                    value={monthFilter}
                    onChange={(value) => {
                      setMonthFilter(value);
                      setCurrentPage(1);
                    }}
                  />
                </Box>
              </InlineStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Invoice Table */}
        <Layout.Section>
          <Card>
            {loading ? (
              <Box padding="600">
                <InlineStack align="center" blockAlign="center" gap="400">
                  <Spinner size="small" />
                  <Text>Loading invoices...</Text>
                </InlineStack>
              </Box>
            ) : (
              <IndexTable
                resourceName={{ singular: 'invoice', plural: 'invoices' }}
                itemCount={invoices.length || 1}
                selectable={false}
                headings={headingsFor(INVOICE_COLUMNS)}
                pagination={{
                  hasNext: currentPage < totalPages,
                  hasPrevious: currentPage > 1,
                  onNext: () => setCurrentPage(p => p + 1),
                  onPrevious: () => setCurrentPage(p => p - 1),
                }}
              >
                {invoices.length === 0 ? (
                  <IndexTable.Row id="empty-invoices" position={0}>
                    <IndexTable.Cell colSpan={INVOICE_COLUMNS.length}>
                      <Box padding="500">
                        <BlockStack gap="200" inlineAlign="center">
                          <Text alignment="center" tone="subdued" variant="bodySm">
                            No invoice yet. They are generated on the 1st of each month for every
                            configured brand — each row will be one brand-month.
                          </Text>
                          {/* The action from the EmptyState this replaced. That component
                              swapped out the whole table, headings included, so the columns
                              vanished; the action itself was worth keeping. */}
                          <Button onClick={openTriggerModal}>
                            Trigger Monthly Invoices
                          </Button>
                        </BlockStack>
                      </Box>
                    </IndexTable.Cell>
                  </IndexTable.Row>
                ) : rowMarkup}
              </IndexTable>
            )}
            {/* Rows per page under the list. The pager stays where it is, in IndexTable's own
                footer, and this sits below it — shown only once there are invoices, and kept
                at size 50 where the pager may disappear, because it is the way back to a
                shorter window. Only the LIST gets this: the two tables inside the invoice
                detail modal below describe one invoice, where a row count means nothing. */}
            {!loading && invoices.length > 0 && (
              <Box padding="300">
                <InlineStack align="start" blockAlign="center">
                  <PageSizeSelect value={itemsPerPage} onChange={handlePageSizeChange} />
                </InlineStack>
              </Box>
            )}
          </Card>
        </Layout.Section>
      </Layout>

      {/* Trigger Monthly Invoices Modal */}
      <Modal
        open={generateModalActive}
        // Escape, the backdrop and the header X all route here. Left unguarded they close the
        // modal while the request is still running, and the failure then sets an error onto a
        // Banner that is no longer mounted — invisible now, and waiting on the next open.
        onClose={triggerLoading ? () => {} : closeTriggerModal}
        title="Trigger Monthly Invoice Generation"
        primaryAction={{
          content: 'Trigger Generation',
          onAction: handleTriggerMonthlyInvoices,
          loading: triggerLoading,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: closeTriggerModal,
            disabled: triggerLoading,
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {triggerError && (
              <Banner tone="critical" onDismiss={() => setTriggerError('')}>
                {triggerError}
              </Banner>
            )}

            <Text>
              {triggerForm.brandId
                ? 'This will generate the invoice for the selected brand and month from its billing report. If the month has no report, for example because there were no sales, one is built first.'
                : 'This will trigger invoice generation for all brands with complete invoice configuration. Invoices are generated from existing billing reports; a brand with no report for the month is skipped.'}
            </Text>

            <FormLayout>
              <Select
                label="Brand"
                options={brandOptions}
                value={triggerForm.brandId}
                onChange={(value) => {
                  setTriggerError('');
                  setTriggerForm(prev => ({ ...prev, brandId: value }));
                }}
                helpText="Leave as All Brands to run the monthly sweep."
              />

              <TextField
                label={triggerForm.brandId ? 'Month' : 'Month (optional)'}
                type="month"
                value={triggerForm.monthString}
                onChange={(value) => {
                  setTriggerError('');
                  setTriggerForm(prev => ({ ...prev, monthString: value }));
                }}
                helpText={triggerForm.brandId
                  ? 'Required when a single brand is selected. Format: YYYY-MM'
                  : 'Leave empty to generate for the previous month. Format: YYYY-MM'}
                placeholder="e.g., 2024-12"
                requiredIndicator={Boolean(triggerForm.brandId)}
              />
            </FormLayout>

            <Banner tone="info">
              <Text>
                {triggerForm.brandId
                  ? 'A brand that already has an invoice for this month will not get a second one. Change an existing draft with Regenerate instead.'
                  : 'Invoices are automatically generated on the 1st of each month. Use this to manually trigger generation if needed. Brands that already have an invoice for the month are skipped, not replaced.'}
              </Text>
            </Banner>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Send Invoice Modal */}
      <Modal
        open={sendModalActive}
        onClose={() => { setSendModalActive(false); setSendRecipients(''); }}
        title={`Send Invoice - ${selectedInvoice?.invoiceNumber || ''}`}
        primaryAction={{
          content: 'Send Invoice',
          icon: EmailIcon,
          onAction: handleSendInvoice,
          loading: actionLoading,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => { setSendModalActive(false); setSendRecipients(''); },
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text as="p">
              Email this invoice to <Text as="span" fontWeight="semibold">
                {selectedInvoice?.brand?.name || 'the brand'}
              </Text>. The invoice PDF is attached automatically.
            </Text>
            <TextField
              label="Send to (optional)"
              value={sendRecipients}
              onChange={setSendRecipients}
              autoComplete="off"
              placeholder="finance@brand.com, ap@brand.com"
              helpText="Leave empty to use the brand's configured invoice recipients. Separate multiple addresses with commas — entering addresses here sends this one invoice to them without changing the brand's settings."
            />
            <Banner tone="info">
              Sending marks the invoice as Sent. It stays editable only until then.
            </Banner>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Record Payment Modal */}
      <Modal
        open={paymentModalActive}
        onClose={() => setPaymentModalActive(false)}
        title={`Record Payment - ${selectedInvoice?.invoiceNumber || ''}`}
        primaryAction={{
          content: 'Record Payment',
          icon: CashDollarIcon,
          onAction: handleRecordPayment,
          loading: actionLoading,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setPaymentModalActive(false),
          }
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Payment Method"
              value={paymentForm.paymentMethod}
              onChange={(value) => setPaymentForm(prev => ({ ...prev, paymentMethod: value }))}
              placeholder="e.g., Bank Transfer, Check, Wire Transfer"
            />

            <TextField
              label="Reference Number"
              value={paymentForm.reference}
              onChange={(value) => setPaymentForm(prev => ({ ...prev, reference: value }))}
              placeholder="Transaction ID, check number, etc."
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Invoice Details Modal */}
      <Modal
        size='fullScreen'
        open={detailsModalActive}
        onClose={() => {
          setDetailsModalActive(false);
          setSelectedInvoice(null);
        }}
        title={`Invoice Preview - ${selectedInvoice?.invoiceNumber || ''}`}
        primaryAction={
          selectedInvoice?.status === 'DRAFT' ? {
            content: 'Approve Invoice',
            icon: CheckIcon,
            onAction: () => handleApproveInvoice(selectedInvoice.id),
            loading: actionLoading,
          } : selectedInvoice?.status === 'APPROVED' ? {
            content: 'Send Invoice',
            icon: EmailIcon,
            onAction: () => {
              setDetailsModalActive(false);
              setSendModalActive(true);
            },
          } : undefined
        }
        secondaryActions={[
          {
            content: pdfDownloadingId === selectedInvoice?.id ? 'Downloading...' : 'Download PDF',
            icon: ReceiptIcon,
            onAction: () => handleDownloadPdf(selectedInvoice?.id, selectedInvoice?.invoiceNumber),
            loading: pdfDownloadingId === selectedInvoice?.id,
            disabled: pdfDownloadingId !== null,
          },
          // DRAFT and APPROVED only. CANCELLED is not reachable from SENT in the status
          // machine, so offering it on a sent invoice always returned a 500.
          ...(selectedInvoice?.status === 'DRAFT' || selectedInvoice?.status === 'APPROVED' ? [{
            content: 'Cancel Invoice',
            icon: DeleteIcon,
            destructive: true,
            onAction: () => handleCancelInvoice(selectedInvoice?.id),
          }] : []),
          {
            content: 'Close',
            onAction: () => {
              setDetailsModalActive(false);
              setSelectedInvoice(null);
            },
          }
        ]}
      >
        {selectedInvoice && (
          <Modal.Section>
            <div style={{ margin: '0 auto', padding: '30px 20px' }}>
              <BlockStack gap="800">
                {/* Status Badge */}
                <InlineStack gap="400" align="space-between">
                  {getStatusBadge(selectedInvoice.status)}
                </InlineStack>

                {/* What 2W changed on this invoice by itself. Console only - not on the PDF. */}
                {(selectedInvoice.autoUpdates || []).length > 0 && (
                  <BlockStack gap="200">
                    {selectedInvoice.autoUpdates.map((update) => {
                      const said = describeAutoUpdate(
                        update,
                        { currency: (a) => formatCurrency(a), date: formatDate },
                        selectedInvoice.status,
                      );
                      return (
                        <Banner key={update.id} tone={said.tone} title={said.title}>
                          <BlockStack gap="100">
                            {said.lines.map((line) => <Text key={line} as="p" variant="bodySm">{line}</Text>)}
                          </BlockStack>
                        </Banner>
                      );
                    })}
                  </BlockStack>
                )}

                {/* Invoice Header - Matching PDF Layout */}
                <Box background="bg-surface-secondary" padding="800" borderRadius="300">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '60px' }}>
                    {/* Left Side - Logo and Invoice Info */}
                    <div style={{ flex: '1' }}>
                      {/* 2W Logo */}
                      <div style={{ marginBottom: '24px' }}>
                        <img src={`${import.meta.env.VITE_API_BASE_URL}/assets/logo`} alt="2W" style={{ height: '50px', width: 'auto' }} />
                      </div>

                      {/* Invoice Details */}
                      <div style={{ fontSize: '15px', lineHeight: '2' }}>
                        <p style={{ margin: '0 0 8px 0' }}><strong>Invoice No. {selectedInvoice.invoiceNumber}</strong></p>
                        <p style={{ margin: '0 0 8px 0' }}>Issue date: {formatDate(selectedInvoice.issueDate)}</p>
                        <p style={{ margin: '0 0 8px 0' }}>Due date: {formatDate(selectedInvoice.dueDate)}</p>
                        <p style={{ margin: '0' }}>Invoice Period: {formatDate(selectedInvoice.periodStart)} to {formatDate(selectedInvoice.periodEnd)}</p>
                      </div>
                    </div>
                    {/* Company Info - Left aligned */}
                    <div style={{ textAlign: 'left', fontSize: '15px', lineHeight: '2', flex: '1' }}>
                      {/* Company Address Block */}
                      <div style={{ marginBottom: '20px' }}>
                        <p style={{ margin: '0 0 8px 0', fontWeight: '600', fontSize: '16px' }}>{selectedInvoice.companyName || selectedInvoice.brand?.name || '-'}</p>
                        {selectedInvoice.companyAddress && (
                          <p style={{ margin: '0 0 8px 0', whiteSpace: 'pre-line' }}>{selectedInvoice.companyAddress}</p>
                        )}
                        {selectedInvoice.companyPhone && (
                          <p style={{ margin: '0' }}>{selectedInvoice.companyPhone}</p>
                        )}
                      </div>


                      {/* Company Registration Block */}
                      <div>
                        {selectedInvoice.registrationNumber && (
                          <p style={{ margin: '0 0 8px 0' }}>Company Registration Number: {selectedInvoice.registrationNumber}</p>
                        )}
                        {selectedInvoice.taxNumber && (
                          <p style={{ margin: '0' }}>Tax number: {selectedInvoice.taxNumber}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </Box>

                <Divider />

                {/* What the month's refunds were made of.
                    A return had goods come back and carries a return fee; a price adjustment
                    is the retailer giving money off an order the customer kept, and carries
                    none. Both sides add up to the refund total already shown below, so this
                    explains that figure rather than adding to it.
                    Not the same thing as the Adjustments row in the summary below, which is a
                    commission adjustment entered by hand in the console. */}
                {(parseFloat(selectedInvoice.refundAmount) || 0) > 0 && (
                  <>
                    <BlockStack gap="300">
                      <Text variant="headingMd" fontWeight="semibold">Refunds</Text>
                      <Box background="bg-surface-secondary" padding="400" borderRadius="200">
                        {((selectedInvoice.returnCount || 0) + (selectedInvoice.adjustmentCount || 0)) === 0 ? (
                          // A month last generated before refunds were split carries the column
                          // default on both sides. Two zeros would read as "no refunds", which
                          // is not what they mean.
                          <Text tone="subdued">
                            This invoice was generated before returns and price adjustments were
                            recorded separately. Regenerate it to see the split.
                          </Text>
                        ) : (
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '15px' }}>
                            <tbody>
                              <tr style={{ borderBottom: '1px solid #e1e3e5' }}>
                                <td style={{ padding: '8px 12px' }}>
                                  Returns
                                  <Text as="span" tone="subdued"> — goods came back, return fee applies</Text>
                                </td>
                                <td style={{ textAlign: 'right', padding: '8px 12px', width: '15%' }}>
                                  {selectedInvoice.returnCount || 0}
                                </td>
                                <td style={{ textAlign: 'right', padding: '8px 12px', width: '20%' }}>
                                  {formatCurrency(selectedInvoice.returnRefundAmount || 0)}
                                </td>
                              </tr>
                              <tr style={{ borderBottom: '1px solid #e1e3e5' }}>
                                <td style={{ padding: '8px 12px' }}>
                                  Price adjustments
                                  <Text as="span" tone="subdued"> — nothing came back, no return fee</Text>
                                </td>
                                <td style={{ textAlign: 'right', padding: '8px 12px' }}>
                                  {selectedInvoice.adjustmentCount || 0}
                                </td>
                                <td style={{ textAlign: 'right', padding: '8px 12px' }}>
                                  {formatCurrency(selectedInvoice.adjustmentRefundAmount || 0)}
                                </td>
                              </tr>
                              <tr style={{ fontWeight: '600' }}>
                                <td style={{ padding: '8px 12px' }}>Total refunded</td>
                                <td style={{ textAlign: 'right', padding: '8px 12px' }}>
                                  {(selectedInvoice.returnCount || 0) + (selectedInvoice.adjustmentCount || 0)}
                                </td>
                                <td style={{ textAlign: 'right', padding: '8px 12px' }}>
                                  {formatCurrency(selectedInvoice.refundAmount)}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        )}
                      </Box>
                    </BlockStack>

                    <Divider />
                  </>
                )}

                {/* Invoice Summary - Fees Owed to 2W (Clean 2-column table) */}
                <BlockStack gap="500">
                  <Text variant="headingMd" fontWeight="semibold">Invoice Summary</Text>
                  <Box background="bg-surface-secondary" padding="600" borderRadius="200">
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '15px'}}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #e1e3e5' }}>
                            <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: '600', width: '70%' }}>Description</th>
                            <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: '600', width: '30%' }}>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr style={{ borderBottom: '1px solid #e1e3e5' }}>
                            <td style={{ padding: '8px 12px' }}>Marketplace Commission Fees</td>
                            <td style={{ textAlign: 'right', padding: '8px 12px' }}>{formatCurrency(selectedInvoice.marketplaceCommissionOnOrders || 0)}</td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #e1e3e5' }}>
                            <td style={{ padding: '8px 12px' }}>2W Commission Fees</td>
                            <td style={{ textAlign: 'right', padding: '8px 12px' }}>{formatCurrency(selectedInvoice.twCommissionOnOrders || 0)}</td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #e1e3e5' }}>
                            <td style={{ padding: '8px 12px' }}>Shipping Fees</td>
                            <td style={{ textAlign: 'right', padding: '8px 12px' }}>{formatCurrency(selectedInvoice.totalShippingFees)}</td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #e1e3e5' }}>
                            <td style={{ padding: '8px 12px' }}>Return Fees</td>
                            <td style={{ textAlign: 'right', padding: '8px 12px' }}>{formatCurrency(selectedInvoice.totalReturnFees)}</td>
                          </tr>
                          {hasStorage(selectedInvoice) && (
                            <tr style={{ borderBottom: '1px solid #e1e3e5' }}>
                              <td style={{ padding: '8px 12px' }}>
                                Storage Fees
                                <div style={{ fontSize: '12px', color: '#6d7175' }}>{storageHint(selectedInvoice)}</div>
                              </td>
                              <td style={{ textAlign: 'right', padding: '8px 12px' }}>{formatCurrency(storageFeeOf(selectedInvoice))}</td>
                            </tr>
                          )}
                          <tr style={{ borderBottom: '1px solid #e1e3e5' }}>
                            <td style={{ padding: '8px 12px' }}>Adjustments</td>
                            <td style={{ textAlign: 'right', padding: '8px 12px' }}>
                              {formatCurrency(selectedInvoice.commissionAdjustments || 0)}
                            </td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #ddd' }}>
                            <td style={{ padding: '12px', fontWeight: '600' }}>Subtotal</td>
                            <td style={{ textAlign: 'right', padding: '12px', fontWeight: '600' }}>
                              {formatCurrency(
                                (parseFloat(selectedInvoice.commissionOnOrders) || 0) +
                                (parseFloat(selectedInvoice.totalShippingFees) || 0) +
                                (parseFloat(selectedInvoice.totalReturnFees) || 0) +
                                storageFeeOf(selectedInvoice) +
                                (parseFloat(selectedInvoice.commissionAdjustments) || 0)
                              )}
                            </td>
                          </tr>
                          {(selectedInvoice.vatRate || 0) > 0 && (
                            <tr style={{ borderBottom: '1px solid #e1e3e5' }}>
                              <td style={{ padding: '8px 12px' }}>VAT ({selectedInvoice.vatRate}%)</td>
                              <td style={{ textAlign: 'right', padding: '8px 12px' }}>{formatCurrency(selectedInvoice.vatAmount || 0)}</td>
                            </tr>
                          )}
                          <tr style={{ backgroundColor: '#f6f6f7', fontWeight: '700', borderTop: '2px solid #333' }}>
                            <td style={{ padding: '12px', fontSize: '15px' }}>TOTAL PAYABLE TO 2W</td>
                            <td style={{ textAlign: 'right', padding: '12px', fontSize: '15px' }}>{formatCurrency(selectedInvoice.totalAmount)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </Box>
                </BlockStack>

                {/* Marketplace-Level Breakdown - Net payout explanation */}
                {selectedInvoice.marketplaceBreakdown && selectedInvoice.marketplaceBreakdown.length > 0 && (
                  <>
                    <Divider />
                    <BlockStack gap="500">
                      <Text variant="headingMd" fontWeight="semibold">Marketplace-Level Breakdown</Text>
                      <Box background="bg-surface-secondary" padding="600" borderRadius="200">
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '1000px' }}>
                            <thead>
                              <tr style={{ borderBottom: '2px solid #e1e3e5' }}>
                                <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: '600' }}>Marketplace</th>
                                <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: '600' }}>Orders</th>
                                <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: '600' }}>Refunds</th>
                                <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: '600' }}>Payable Orders</th>
                                <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: '600' }}>Marketplace Commission</th>
                                <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: '600' }}>2W Commission</th>
                                <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: '600' }}>Shipping</th>
                                <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: '600' }}>Return Fees</th>
                                <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: '600' }}>Adjustments</th>
                                <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: '600' }}>Net to Merchant</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedInvoice.marketplaceBreakdown.map((mp, idx) => {
                                const mpGrossSales = parseFloat(mp.grossSales) || 0;
                                const mpRefunds = parseFloat(mp.refundAmount || mp.refunds) || 0;
                                const mpPayableOrders = mpGrossSales - mpRefunds;
                                const mpMktCommission = parseFloat(mp.marketplaceCommissionAmount) || 0;
                                // `||`, not `??`: parseFloat answers NaN rather than null for a missing value, so `??`
    // never fired and NaN reached the page. And no fall back to the COMBINED charge, which
    // printed the marketplace fee again on the 2W line.
    const mpTwCommission = parseFloat(mp.twCommissionAmount) || 0;
                                const mpTotalCommission = mpMktCommission + mpTwCommission;
                                const mpShipping = parseFloat(mp.shippingFees) || 0;
                                const mpReturnFees = parseFloat(mp.returnFees) || 0;
                                const mpNetToMerchant = mpPayableOrders - mpTotalCommission - mpShipping - mpReturnFees;
                                return (
                                  <tr key={`mp-${idx}`} style={{ borderBottom: '1px solid #e1e3e5' }}>
                                    <td style={{ padding: '8px 8px' }}>{mp.marketplace}</td>
                                    <td style={{ textAlign: 'right', padding: '8px 8px' }}>{formatCurrency(mpGrossSales)}</td>
                                    <td style={{ textAlign: 'right', padding: '8px 8px' }}>{formatNegativeCurrency(mpRefunds)}</td>
                                    <td style={{ textAlign: 'right', padding: '8px 8px' }}>{formatCurrency(mpPayableOrders)}</td>
                                    <td style={{ textAlign: 'right', padding: '8px 8px' }}>{formatNegativeCurrency(mpMktCommission)}</td>
                                    <td style={{ textAlign: 'right', padding: '8px 8px' }}>{formatNegativeCurrency(mpTwCommission)}</td>
                                    <td style={{ textAlign: 'right', padding: '8px 8px' }}>{formatNegativeCurrency(mpShipping)}</td>
                                    <td style={{ textAlign: 'right', padding: '8px 8px' }}>{formatNegativeCurrency(mpReturnFees)}</td>
                                    <td style={{ textAlign: 'right', padding: '8px 8px' }}></td>
                                    <td style={{ textAlign: 'right', padding: '8px 8px' }}>{formatCurrency(mpNetToMerchant)}</td>
                                  </tr>
                                );
                              })}
                              {/* ADJUSTMENTS */}
                              <tr>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td style={{ textAlign: 'right', padding: '10px 8px' }}>
                                  USD {(selectedInvoice.commissionAdjustments || 0) < 0 ? '' : '-'}{Math.abs(selectedInvoice.commissionAdjustments || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td style={{ textAlign: 'right', padding: '10px 8px' }}>
                                  USD {(selectedInvoice.commissionAdjustments || 0) < 0 ? '' : '-'}{Math.abs(selectedInvoice.commissionAdjustments || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                              </tr>
                              {hasStorage(selectedInvoice) && (
                                <tr>
                                  <td style={{ padding: '10px 8px' }}>Storage Fees</td>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                  <td style={{ textAlign: 'right', padding: '10px 8px' }}>{formatNegativeCurrency(storageFeeOf(selectedInvoice))}</td>
                                </tr>
                              )}
                              {/* TOTALS Row */}
                              <tr style={{ borderTop: '2px solid #333', fontWeight: '600' }}>
                                <td style={{ padding: '10px 8px' }}><strong>TOTALS</strong></td>
                                <td style={{ textAlign: 'right', padding: '10px 8px' }}><strong>{formatCurrency(selectedInvoice.grossSales)}</strong></td>
                                <td style={{ textAlign: 'right', padding: '10px 8px' }}><strong>{formatNegativeCurrency(selectedInvoice.refundAmount)}</strong></td>
                                <td style={{ textAlign: 'right', padding: '10px 8px' }}><strong>{formatCurrency((parseFloat(selectedInvoice.grossSales) || 0) - (parseFloat(selectedInvoice.refundAmount) || 0))}</strong></td>
                                <td style={{ textAlign: 'right', padding: '10px 8px' }}><strong>{formatNegativeCurrency(selectedInvoice.marketplaceCommissionOnOrders || 0)}</strong></td>
                                <td style={{ textAlign: 'right', padding: '10px 8px' }}><strong>{formatNegativeCurrency(selectedInvoice.twCommissionOnOrders || 0)}</strong></td>
                                <td style={{ textAlign: 'right', padding: '10px 8px' }}><strong>{formatNegativeCurrency(selectedInvoice.totalShippingFees)}</strong></td>
                                <td style={{ textAlign: 'right', padding: '10px 8px' }}><strong>{formatNegativeCurrency(selectedInvoice.totalReturnFees)}</strong></td>
                                <td style={{ textAlign: 'right', padding: '10px 8px' }}>
                                  <strong>USD {(selectedInvoice.commissionAdjustments || 0) < 0 ? '' : '-'}{Math.abs(selectedInvoice.commissionAdjustments || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                                </td>
                                <td style={{ textAlign: 'right', padding: '10px 8px' }}>
                                  <strong>{formatCurrency(
                                    ((parseFloat(selectedInvoice.grossSales) || 0) - (parseFloat(selectedInvoice.refundAmount) || 0)) -
                                    (parseFloat(selectedInvoice.commissionOnOrders) || 0) -
                                    (parseFloat(selectedInvoice.totalShippingFees) || 0) -
                                    (parseFloat(selectedInvoice.totalReturnFees) || 0) -
                                    storageFeeOf(selectedInvoice) -
                                    (parseFloat(selectedInvoice.commissionAdjustments) || 0)
                                  )}</strong>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {/* Amount to be transferred to merchant */}
                        <Box paddingBlockStart="500" paddingBlockEnd="200">
                          <div style={{ borderTop: '2px solid #333', paddingTop: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text variant="headingMd" fontWeight="bold">AMOUNT TO BE TRANSFERRED TO MERCHANT:</Text>
                            <Text variant="headingMd" fontWeight="bold">
                              {formatCurrency(
                                ((parseFloat(selectedInvoice.grossSales) || 0) - (parseFloat(selectedInvoice.refundAmount) || 0)) -
                                (parseFloat(selectedInvoice.commissionOnOrders) || 0) -
                                (parseFloat(selectedInvoice.totalShippingFees) || 0) -
                                (parseFloat(selectedInvoice.totalReturnFees) || 0) -
                                storageFeeOf(selectedInvoice) -
                                (parseFloat(selectedInvoice.commissionAdjustments) || 0)
                              )}
                            </Text>
                          </div>
                        </Box>

                        {/* Footnote */}
                        <Box paddingBlockStart="400">
                          <Text variant="bodySm" tone="subdued" fontStyle="italic">
                            (3) Includes refunds for previous periods and the current period. Refund processing date determines the reporting period.
                          </Text>
                        </Box>
                      </Box>
                    </BlockStack>
                  </>
                )}

                {/* Payment Info */}
                {selectedInvoice.status === 'PAID' && (
                  <>
                    <Divider />
                    <BlockStack gap="300">
                      <Text variant="headingSm">Payment Information</Text>
                      <InlineStack gap="600">
                        <BlockStack gap="100">
                          <Text variant="bodySm" tone="subdued">Payment Date</Text>
                          <Text variant="bodyMd">{formatDate(selectedInvoice.paidAt)}</Text>
                        </BlockStack>
                        <BlockStack gap="100">
                          <Text variant="bodySm" tone="subdued">Payment Method</Text>
                          <Text variant="bodyMd">{selectedInvoice.paymentMethodText || '-'}</Text>
                        </BlockStack>
                        <BlockStack gap="100">
                          <Text variant="bodySm" tone="subdued">Reference</Text>
                          <Text variant="bodyMd">{selectedInvoice.paymentReference || '-'}</Text>
                        </BlockStack>
                      </InlineStack>
                    </BlockStack>
                  </>
                )}

                {/* A "Bank Details" block used to sit here, gated on
                    selectedInvoice.bankAccountNumber / routingNumber and masked through a
                    maskAccountNumber helper. Neither field exists on the Invoice model or
                    anywhere in the backend, so the gate was never true and the block never
                    rendered. Its footnote also promised "Full bank details are available in
                    the PDF invoice", which pdf-service.js does not emit. Removed along with
                    the now-unused helper. Reinstate only once the fields actually exist in
                    the schema and the PDF. */}
              </BlockStack>
            </div>
          </Modal.Section>
        )}
      </Modal>
    </Page >
  );
}
